import type { LlmCostAccounting } from '@edgevanta/bid-tab-extract';
import type { LanguageModel } from 'ai';

import type { CategoryCandidate } from '../item-classification';
import { classifyLines, type UnitResolution } from './classify-lines';

// Orchestrates on-the-fly classification for one persisted custom proposal:
// retrieve candidates → classify with the dbt prompt → resolve codes to ids →
// write the base layer. Depends on the domains repo only structurally, so this
// stays decoupled (ai → domains is allowed; this avoids the class import).

/** The subset of the custom-proposals repository this orchestrator needs. */
export interface ClassificationRepo {
  listUnclassifiedProposalItems(
    proposalId: string,
  ): Promise<
    Array<{ itemId: string; description: string; unit: string | null }>
  >;
  findCategoryCandidates(
    items: Array<{ description: string; unit: string | null }>,
    options?: { topK?: number; threshold?: number },
  ): Promise<Array<CategoryCandidate[]>>;
  resolveCategoryIdsByCode(codes: string[]): Promise<Map<string, string>>;
  resolveCollectionIdsByCode(codes: string[]): Promise<Map<string, string>>;
  resolveUnitsByInput(units: string[]): Promise<Map<string, UnitResolution>>;
  applyItemEnrichments(
    updates: Array<{
      itemId: string;
      categoryId?: string | null;
      isTrueLumpSum?: boolean;
      unitId?: string | null;
      collections?: Array<{
        collectionId: string;
        sourceUom: string;
        sourceUnitId?: string | null;
        conversionFactor: number;
      }>;
    }>,
  ): Promise<void>;
}

export interface ClassifyProposalResult {
  total: number;
  /** Items that got a real category. */
  classified: number;
  /** Items that came back uncategorizable ('99' / unresolved). */
  novel: number;
}

/**
 * Classify every still-unclassified custom item on a proposal and write the
 * base layer (category, lump-sum, unit on `items`; collections as mappings).
 * Returns a small summary for logging / the CLI. The model is pluggable; the
 * default lives in {@link classifyBidTabItems}.
 */
export async function classifyProposalItems(
  proposalId: string,
  repo: ClassificationRepo,
  options: {
    model?: LanguageModel;
    novelModel?: LanguageModel;
    costAccounting?: LlmCostAccounting;
  } = {},
): Promise<ClassifyProposalResult> {
  const items = await repo.listUnclassifiedProposalItems(proposalId);
  if (items.length === 0) {
    return { total: 0, classified: 0, novel: 0 };
  }

  const classified = await classifyLines(
    items.map((it) => ({ description: it.description, unit: it.unit })),
    repo,
    options,
  );

  let classifiedCount = 0;
  let novel = 0;
  const updates = items.map((it, i) => {
    const c = classified[i];
    if (c?.categoryId) {
      classifiedCount += 1;
    } else {
      novel += 1;
    }
    return {
      itemId: it.itemId,
      categoryId: c?.categoryId ?? null,
      isTrueLumpSum: c?.isTrueLumpSum ?? false,
      unitId: c?.unitId ?? null,
      collections: (c?.collections ?? []).map((col) => ({
        collectionId: col.collectionId,
        sourceUom: col.sourceUom,
        sourceUnitId: col.sourceUnitId,
        conversionFactor: col.conversionFactor,
      })),
    };
  });

  await repo.applyItemEnrichments(updates);
  return { total: items.length, classified: classifiedCount, novel };
}
